/**
 * Cliente API de compensaciones (Sprint 13 S13-05, migrado desde
 * frontend-superadmin tras la doctrina Portal vs Superadmin del
 * 2026-05-13).
 *
 * - GET /compensaciones/?activa=true para selector.
 * - POST /compensaciones/ para crear (S12-18 patch).
 * - POST /compensaciones/{id}/aplicar para ejecutar el flujo atomico
 *   de pago en especie (Sprint 12 S12-14).
 */
import { apiFetch } from './client';
import type {
  AplicarCompensacionInput,
  AplicarCompensacionResponse,
  CerrarCompensacionInput,
  CompensacionConDetalle,
  CompensacionCreate,
  CompensacionRead,
  CompensacionUpdate,
  PreviewAplicacionResponse,
} from './types';

export function listCompensaciones(opts: {
  activa?: boolean;
  lote_id?: string;
} = {}): Promise<CompensacionRead[]> {
  const params = new URLSearchParams();
  if (opts.activa !== undefined) params.set('activa', String(opts.activa));
  if (opts.lote_id) params.set('lote_id', opts.lote_id);
  const qs = params.toString();
  return apiFetch<CompensacionRead[]>(`/compensaciones/${qs ? `?${qs}` : ''}`);
}

/**
 * Sprint 17 Fase 2 — lista enriquecida para la UI de cards.
 * Backend: GET /compensaciones/con-detalle.
 */
export function listCompensacionesConDetalle(opts: {
  activa?: boolean;
  lote_id?: string;
} = {}): Promise<CompensacionConDetalle[]> {
  const params = new URLSearchParams();
  if (opts.activa !== undefined) params.set('activa', String(opts.activa));
  if (opts.lote_id) params.set('lote_id', opts.lote_id);
  const qs = params.toString();
  return apiFetch<CompensacionConDetalle[]>(
    `/compensaciones/con-detalle${qs ? `?${qs}` : ''}`,
  );
}

export function createCompensacion(
  data: CompensacionCreate,
): Promise<CompensacionRead> {
  return apiFetch<CompensacionRead>('/compensaciones/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function aplicarCompensacion(
  compensacionId: string,
  data: AplicarCompensacionInput,
): Promise<AplicarCompensacionResponse> {
  return apiFetch<AplicarCompensacionResponse>(
    `/compensaciones/${compensacionId}/aplicar`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  );
}

/**
 * Sprint 17 Z8 — preview greedy del 'Aplicar' sin escribir nada.
 * Usado por el modal de Aplicar para mostrar tabla read-only.
 */
export function previewAplicacionCompensacion(
  compensacionId: string,
): Promise<PreviewAplicacionResponse> {
  return apiFetch<PreviewAplicacionResponse>(
    `/compensaciones/${compensacionId}/preview-aplicacion`,
  );
}

/** Sprint 17 Fase 2 — PUT /compensaciones/{id} */
export function actualizarCompensacion(
  compensacionId: string,
  data: CompensacionUpdate,
): Promise<CompensacionRead> {
  return apiFetch<CompensacionRead>(`/compensaciones/${compensacionId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/** Sprint 17 Fase 2 — POST /compensaciones/{id}/cerrar */
export function cerrarCompensacion(
  compensacionId: string,
  data: CerrarCompensacionInput,
): Promise<CompensacionRead> {
  return apiFetch<CompensacionRead>(
    `/compensaciones/${compensacionId}/cerrar`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  );
}

/** Sprint 17 Fase 2 — DELETE /compensaciones/{id} */
export function eliminarCompensacion(compensacionId: string): Promise<void> {
  return apiFetch<void>(`/compensaciones/${compensacionId}`, {
    method: 'DELETE',
  });
}
